import { useSignIn } from "@solana/react"
import type { SolanaSignInInput, SolanaSignInOutput } from "@solana/wallet-standard-features"
import * as solanaWalletStd from "@solana/wallet-standard-features"
import type { UiWallet, UiWalletAccount } from "@wallet-standard/react"
import { useWallets } from "@wallet-standard/react"
import React, { useCallback, useRef, useState } from "react"
import { ErrorBoundary } from "react-error-boundary"
import { gql } from "~/__generated__"
import { Button } from "~/ui/button"
import { useToast } from "~/ui/use-toast"
import { useSafeMutation } from "~/common/use-safe-mutation"

import { ErrorDialog } from "./ErrorDialog"
import { UnconnectableWalletMenuItem } from "./UnconnectableWalletMenuItem"
import { WalletMenuItemContent } from "./WalletMenuItemContent"
import info from "~/images/icons/info"

const SOLANA_SIGN_IN_PREPARE = gql(`
  mutation SolanaSignInPrepare($input: SolanaSignInPrepareInput!) {
    solanaSignInPrepare(input: $input) {
      domain
      statement
      uri
      version
      nonce
      issuedAt
    }
  }
`)

const SOLANA_SIGN_IN_VERIFY = gql(`
  mutation SolanaSignInVerify($input: SolanaSignInVerifyInput!) {
    solanaSignInVerify(input: $input) {
      user {
        id
        email
      }
      csrfToken
    }
  }
`)

const SOLANA_ADD_WALLET_PREPARE = gql(`
  mutation SolanaAddWalletPrepare($input: SolanaAddWalletPrepareInput!) {
    solanaAddWalletPrepare(input: $input) {
      domain
      statement
      uri
      version
      nonce
      issuedAt
    }
  }
`)

const SOLANA_ADD_WALLET_VERIFY = gql(`
  mutation SolanaAddWalletVerify($input: SolanaAddWalletVerifyInput!) {
    solanaAddWalletVerify(input: $input) {
      userWallet {
        id
        publicKey
        provider
        providerDisplayName
      }
    }
  }
`)

type WalletConnectMenuItemProps = Readonly<{
  onError(err: unknown): void
  onSuccess(account: UiWalletAccount | undefined): void
  wallet: UiWallet
  mode: "signin" | "add_wallet"
  disabled?: boolean
}>

function WalletConnectMenuItem({
  onSuccess,
  onError,
  wallet,
  mode,
  disabled,
}: WalletConnectMenuItemProps) {
  const { toast } = useToast()
  const signIn = useSignIn(wallet)
  const [isConnecting, setIsConnecting] = useState(false)

  const [solanaSignInPrepare] = useSafeMutation(SOLANA_SIGN_IN_PREPARE)
  const [solanaSignInVerify] = useSafeMutation(SOLANA_SIGN_IN_VERIFY)
  const [solanaAddWalletPrepare] = useSafeMutation(SOLANA_ADD_WALLET_PREPARE)
  const [solanaAddWalletVerify] = useSafeMutation(SOLANA_ADD_WALLET_VERIFY)

  const handleConnectClick = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault()
      if (!signIn) {
        onError(new Error("Sign-in feature not available for this wallet."))
        toast({
          title: "Connection failed",
          description: "Sign-in feature not available for this wallet.",
          variant: "destructive",
        })
        return
      }

      setIsConnecting(true)
      try {
        // Step 1: Prepare the challenge
        let prepareData: {
          domain: string
          statement: string
          uri: string
          version: string
          nonce: string
          issuedAt: string
        }

        if (mode === "signin") {
          const prepareResult = await solanaSignInPrepare({
            variables: { input: {} },
          })
          const data = prepareResult.data?.solanaSignInPrepare
          if (!data) {
            throw new Error("Failed to prepare sign-in")
          }
          prepareData = data
        } else {
          const prepareResult = await solanaAddWalletPrepare({
            variables: { input: {} },
          })
          const data = prepareResult.data?.solanaAddWalletPrepare
          if (!data) {
            throw new Error("Failed to prepare wallet addition")
          }
          prepareData = data
        }

        // Convert the challenge data to the format expected by the wallet
        const input: SolanaSignInInput = {
          domain: prepareData.domain,
          statement: prepareData.statement,
          uri: prepareData.uri,
          version: prepareData.version,
          nonce: prepareData.nonce,
          issuedAt: prepareData.issuedAt,
        }

        // Step 2: Get signature from wallet
        const output: SolanaSignInOutput = await signIn(input)

        // Step 3: Verify the signature
        const verifyInput = {
          input: {
            domain: prepareData.domain,
            statement: prepareData.statement,
            uri: prepareData.uri,
            version: prepareData.version,
            nonce: prepareData.nonce,
            issuedAt: prepareData.issuedAt,
            address: output.account.address,
          },
          output: {
            account: {
              address: output.account.address,
              publicKey: Array.from(output.account.publicKey),
            },
            signedMessage: Array.from(output.signedMessage),
            signature: Array.from(output.signature),
            signatureType: output.signatureType ? [output.signatureType] : undefined,
          },
          walletInfo: {
            walletName: wallet.name,
            walletVersion: wallet.version,
          },
        }

        if (mode === "signin") {
          await solanaSignInVerify({
            variables: { input: verifyInput },
          })
        } else {
          await solanaAddWalletVerify({
            variables: { input: verifyInput },
          })
        }

        // Only show toast for wallet addition, not sign-in (parent handles sign-in success)
        if (mode === "add_wallet") {
          toast({ title: "Wallet successfully added!" })
        }

        onSuccess({
          ...output.account,
          "~uiWalletHandle": Symbol("uiWalletHandle"),
        } as UiWalletAccount)
      } catch (e) {
        onError(e)
        const errorTitle = mode === "signin" ? "Sign-in failed" : "Failed to add wallet"
        toast({
          title: errorTitle,
          description: e instanceof Error ? e.message : String(e),
          variant: "destructive",
        })
      } finally {
        setIsConnecting(false)
      }
    },
    [
      signIn,
      onSuccess,
      onError,
      toast,
      mode,
      solanaSignInPrepare,
      solanaSignInVerify,
      solanaAddWalletPrepare,
      solanaAddWalletVerify,
    ]
  )

  const buttonText = mode === "signin" ? "Connect" : "Add"

  return (
    <Button
      onClick={handleConnectClick}
      variant="outline"
      className="h-12 w-full justify-between rounded-lg border-2 border-gray-200 bg-white px-4 hover:border-purple-300"
      disabled={isConnecting || disabled}
    >
      <WalletMenuItemContent loading={isConnecting} wallet={wallet} />
      <span className="rounded bg-purple-600 px-3 py-1 text-sm font-medium text-white">
        {buttonText}
      </span>
    </Button>
  )
}

type SolanaWalletConnectProps = Readonly<{
  children?: React.ReactNode
  mode: "signin" | "add_wallet"
  onSuccess?: (account: UiWalletAccount | undefined) => void
  onError?: (error: unknown) => void
  excludeWallets?: string[] // Array of wallet public keys to exclude
}>

export function SolanaWalletConnect({
  children,
  mode,
  onSuccess,
  onError,
  excludeWallets: _excludeWallets = [],
}: SolanaWalletConnectProps) {
  const { current: NO_ERROR } = useRef(Symbol())
  const wallets = useWallets()
  const [error, setError] = useState<symbol | unknown>(NO_ERROR)

  const handleSuccess = useCallback(
    (account: UiWalletAccount | undefined) => {
      onSuccess?.(account)
    },
    [onSuccess]
  )

  const handleError = useCallback(
    (err: unknown) => {
      setError(err)
      onError?.(err)
    },
    [onError]
  )

  const walletsThatSupportSignInWithSolana = wallets.filter((wallet) =>
    wallet.features.includes(solanaWalletStd.SolanaSignIn)
  )

  return (
    <>
      <div className="space-y-3">
        {children && <div className="text-sm font-medium">{children}</div>}
        {walletsThatSupportSignInWithSolana.length === 0 ? (
          <div className="mx-auto w-4/5">
            <div className="flex items-center gap-3 rounded-lg bg-postal-purple p-4 text-white">
              <img
                src={info.src}
                alt="Alert"
                className="h-5 w-5 flex-shrink-0 brightness-0 invert"
              />
              <div className="text-base font-medium">
                No wallet detected. Visit this page from your mobile wallet app to access
                postal.fun.
              </div>
            </div>
          </div>
        ) : (
          walletsThatSupportSignInWithSolana.map((wallet) => (
            <ErrorBoundary
              fallbackRender={({ error }) => (
                <UnconnectableWalletMenuItem error={error} wallet={wallet} />
              )}
              key={`wallet:${wallet.name}`}
            >
              <WalletConnectMenuItem
                onSuccess={handleSuccess}
                onError={handleError}
                wallet={wallet}
                mode={mode}
              />
            </ErrorBoundary>
          ))
        )}
      </div>
      {error !== NO_ERROR && mode === "add_wallet" ? (
        <ErrorDialog error={error} onClose={() => setError(NO_ERROR)} />
      ) : null}
    </>
  )
}
